import React from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Scroll,
  FileSignature,
  Briefcase,
  Landmark,
  ShieldCheck,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  Gavel,
  Lock,
  Sparkles,
  HeartHandshake,
  Zap,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { OFFICE, whatsappUrl, telUrl, mailUrl, mapUrl, mapEmbedUrl } from "../lib/office";

const heroImage =
  "https://images.pexels.com/photos/48148/document-agreement-documents-sign-48148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const presentationImage =
  "https://images.unsplash.com/photo-1769092992534-f2d0210162b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function Home() {
  const { t } = useLang();

  const services = [
    { icon: Building2, key: "immobilier" },
    { icon: Scroll, key: "successions" },
    { icon: FileSignature, key: "procurations" },
    { icon: Briefcase, key: "societes" },
    { icon: Landmark, key: "garanties" },
    { icon: ShieldCheck, key: "certifications" },
  ];

  const prepareLinks = [
    { fr: "Vente immobilière", ar: "بيع عقاري", cat: "immobilier", msg: t("whatsappMessages.realEstate") },
    { fr: "Succession", ar: "ميراث", cat: "successions", msg: t("whatsappMessages.succession") },
    { fr: "Procuration", ar: "وكالة", cat: "procurations", msg: "" },
    { fr: "Création de société", ar: "تأسيس شركة", cat: "societes", msg: "" },
    { fr: "Hypothèque", ar: "رهن", cat: "garanties", msg: "" },
    { fr: "Contrat divers", ar: "عقود متنوعة", cat: "procurations", msg: "" },
  ];

  const whyPoints = [
    { icon: Gavel, key: "why1" },
    { icon: Lock, key: "why2" },
    { icon: Sparkles, key: "why3" },
    { icon: HeartHandshake, key: "why4" },
    { icon: Zap, key: "why5" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border-light" data-testid="hero-section">
        <div className="container-notary grid grid-cols-1 lg:grid-cols-12 gap-10 py-20 lg:py-28">
          <div className="lg:col-span-7 flex flex-col justify-center animate-fade-up">
            <div className="overline mb-5">{t("home.eyebrow")}</div>
            <h1 className="text-4xl md:text-5xl lg:text-[64px] leading-[1.05] font-serif text-charcoal">
              {t("home.h1")}
            </h1>
            <div className="mt-6 flex items-center gap-4">
              <span className="divider-gold" />
              <p className="font-serif italic text-lg text-charcoal-muted">{t("home.subtitle")}</p>
            </div>
            <p className="mt-8 text-base md:text-lg text-charcoal-muted leading-relaxed max-w-2xl">
              {t("home.heroDesc")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/rendez-vous" className="btn-gold" data-testid="hero-cta-appointment">
                {t("common.bookAppointment")}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
              <a
                href={whatsappUrl(t("whatsappMessages.appointment"))}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-dark"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                {t("common.whatsapp")}
              </a>
              <Link to="/documents" className="btn-ghost" data-testid="hero-cta-documents">
                {t("common.viewDocuments")}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative h-[320px] md:h-[440px] lg:h-[520px] border border-border-light overflow-hidden">
              <img
                src={heroImage}
                alt="Cabinet notarial — signature d'acte"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              <div className="absolute bottom-4 start-4 bg-surface/95 backdrop-blur-sm px-4 py-3 border border-border-light">
                <div className="overline text-[10px]">{t("office.shortRole")}</div>
                <div className="font-serif text-sm text-charcoal">{t("office.tagline")}</div>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-4 -end-4 w-24 h-24 border border-gold" />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-surface-muted border-b border-border-light" data-testid="trust-strip">
        <div className="container-notary grid grid-cols-2 md:grid-cols-5 gap-4 py-6 text-sm">
          <a href={mapUrl()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold">
            <MapPin className="w-4 h-4 text-gold shrink-0" />
            <span className="truncate">{t("office.address")}</span>
          </a>
          <a href={telUrl(true)} className="flex items-center gap-3 hover:text-gold">
            <Phone className="w-4 h-4 text-gold shrink-0" />
            <span>{OFFICE.phoneFixed}</span>
          </a>
          <a href={telUrl(false)} className="flex items-center gap-3 hover:text-gold">
            <MessageCircle className="w-4 h-4 text-gold shrink-0" />
            <span>{OFFICE.phoneMobile}</span>
          </a>
          <a href={mailUrl()} className="flex items-center gap-3 hover:text-gold">
            <Mail className="w-4 h-4 text-gold shrink-0" />
            <span className="truncate">{OFFICE.email}</span>
          </a>
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-gold shrink-0" />
            <span>{t("office.hours")}</span>
          </div>
        </div>
      </section>

      {/* Presentation */}
      <section className="container-notary py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="presentation-section">
        <div className="lg:col-span-5">
          <div className="border border-border-light overflow-hidden h-[360px] md:h-[460px]">
            <img src={presentationImage} alt="Bibliothèque juridique" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          <div className="overline mb-4">{t("office.shortRole")}</div>
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal">{t("home.presentationTitle")}</h2>
          <span className="divider-gold my-6" />
          <p className="text-base text-charcoal-muted leading-relaxed">{t("home.presentationText")}</p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/cabinet" className="btn-outline-dark">
              {t("nav.cabinet")} <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
            <Link to="/services" className="btn-ghost">
              {t("nav.services")} <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="bg-surface-muted border-y border-border-light py-20 md:py-28" data-testid="services-section">
        <div className="container-notary">
          <div className="max-w-2xl">
            <div className="overline mb-4">{t("home.servicesOverlineTitle")}</div>
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal">{t("home.servicesTitle")}</h2>
            <span className="divider-gold my-5 block" />
            <p className="text-charcoal-muted">{t("home.servicesSubtitle")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-light border border-border-light">
            {services.map(({ icon: Icon, key }) => (
              <Link
                key={key}
                to="/services"
                className="group bg-surface p-8 md:p-10 flex flex-col gap-4 transition-colors hover:bg-base"
                data-testid={`service-card-${key}`}
              >
                <Icon className="w-8 h-8 text-gold" strokeWidth={1.2} />
                <h3 className="font-serif text-xl text-charcoal">
                  {t(`services.categories.${key}.title`)}
                </h3>
                <p className="text-sm text-charcoal-muted leading-relaxed">
                  {t(`services.categories.${key}.desc`)}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold group-hover:gap-3 transition-all">
                  {t("common.readMore")}
                  <ArrowRight className="w-3 h-3 rtl-flip" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Prepare your file */}
      <section className="container-notary py-20 md:py-28" data-testid="prepare-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3">{t("documents.eyebrow")}</div>
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal">{t("home.prepareTitle")}</h2>
            <span className="divider-gold my-5 block" />
            <p className="text-charcoal-muted max-w-xl">{t("home.prepareSubtitle")}</p>
          </div>
          <Link to="/documents" className="btn-outline-dark">
            {t("common.viewDocuments")}
            <ArrowRight className="w-4 h-4 rtl-flip" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border-light border border-border-light">
          {prepareLinks.map((p) => (
            <Link
              key={p.fr}
              to={`/documents?cat=${p.cat}`}
              className="bg-surface hover:bg-base p-6 md:p-8 flex items-center justify-between gap-3 group transition-colors"
              data-testid={`prepare-link-${p.cat}`}
            >
              <span className="font-serif text-base md:text-lg text-charcoal">{p.fr}</span>
              <ArrowRight className="w-4 h-4 text-gold rtl-flip group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-charcoal text-white py-20 md:py-28" data-testid="why-section">
        <div className="container-notary">
          <div className="max-w-2xl">
            <div className="overline text-gold mb-3">{t("office.shortRole")}</div>
            <h2 className="text-3xl md:text-4xl font-serif text-white">{t("home.whyTitle")}</h2>
            <span className="inline-block w-10 h-px bg-gold my-5" />
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {whyPoints.map(({ icon: Icon, key }) => (
              <div key={key} className="bg-charcoal p-8 flex flex-col gap-3">
                <Icon className="w-6 h-6 text-gold" strokeWidth={1.2} />
                <h3 className="font-serif text-lg text-white">{t(`home.${key}.title`)}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{t(`home.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strong CTA */}
      <section className="container-notary py-20 md:py-28" data-testid="cta-section">
        <div className="border border-border-light bg-surface p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">{t("common.bookAppointment")}</div>
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal">{t("home.ctaTitle")}</h2>
            <p className="text-charcoal-muted mt-4 max-w-xl">{t("home.ctaDesc")}</p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/rendez-vous" className="btn-gold" data-testid="cta-appointment">
              {t("common.bookAppointment")}
            </Link>
            <a href={telUrl(true)} className="btn-outline-dark" data-testid="cta-call">
              <Phone className="w-4 h-4" />
              {t("common.call")}
            </a>
            <a
              href={whatsappUrl(t("whatsappMessages.general"))}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              data-testid="cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              {t("common.whatsapp")}
            </a>
          </div>
        </div>
      </section>

      {/* Map preview */}
      <section className="border-t border-border-light bg-surface-muted" data-testid="map-preview-section">
        <div className="container-notary py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
          <div className="md:col-span-4 flex flex-col justify-center">
            <div className="overline mb-3">{t("nav.contact")}</div>
            <h2 className="text-2xl md:text-3xl font-serif text-charcoal">{t("home.mapTitle")}</h2>
            <span className="divider-gold my-5 block" />
            <p className="text-charcoal-muted">{t("home.mapSubtitle")}</p>
            <a href={mapUrl()} target="_blank" rel="noopener noreferrer" className="btn-outline-dark mt-6 self-start">
              <MapPin className="w-4 h-4" />
              {t("common.itinerary")}
            </a>
          </div>
          <div className="md:col-span-8 border border-border-light h-[320px] md:h-[420px] overflow-hidden">
            <iframe
              title="Google map"
              src={mapEmbedUrl()}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
